import * as React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Checkbox, NumericTextBox } from '@progress/kendo-react-inputs';
import { fetchApi } from '../../services/api';
import { Title } from '../../utils/title';
import { CompanySettingsViewModel as Settings } from 'TypeGen/CompanySettings/company-settings-view-model';
import { Link } from 'react-router-dom';
import { DropDownList } from '@progress/kendo-react-dropdowns';

type Props = {}

type GetCompanySettings = {
  ResetRules: ResetRule[];
} & Settings;

type ResetRule = {
  MPG: number;
  HaulingCategory: string;
  Rules: string[];
}

const waitingTimeAgentModes = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'On' },
  { value: 2, label: 'Audit' },
];

const loadConfirmationCallAgentModes = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'On' },
];

type State = {
  loading: boolean;
  settings: null|GetCompanySettings;
  vehicles: Array<{ ID: number; Name: string }>;
}

export default class CompanySettings extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: true,
      settings: null,
      vehicles: [],
    };

    this.saveSettings = this.saveSettings.bind(this);
  }

  public componentDidMount() {
    fetchApi('/api/Company/Settings')
    .then((response: GetCompanySettings) => {
      this.setState({
          loading: false,
          settings: response,
      });
    });

    fetchApi("/api/Track/AssetNumbers")
    .then((data: { Drivers: Array<{ ID: number, Name: string }>, Vehicles: Array<{ ID: number, Name: string }> }) => {
      this.setState({ vehicles: data.Vehicles });
    });
  }

  public render() {
    if (this.state.loading || this.state.settings === null) {
      return (
        <span
          className="k-i-loading k-icon"
          style={{ fontSize: 64, position: 'absolute', left: '50%' }}
        />
      );
    }

    return (
      <div className="container card-block k-form k-form-md">
        <Title string="Company Settings" />
        <br />
        <div className='border rounded p-3'>
          <h5>Driver Quote Offer Settings</h5>
          <div className="k-form-field">
            <Checkbox
              label="Allow Alternate Bid (Non-Company Drivers Only)"
              checked={this.state.settings.AssetAlternativeBidEnabled}
              onChange={(e) => this.setSettings({ AssetAlternativeBidEnabled: e.value })}
            />
          </div>
          {/*{this.state.settings.AssetAlternativeBidEnabled && <div className="k-form-field">
            <label>Offer Max % Adjustment of All-In Rate</label>
            <NumericTextBox
              min={0}
              max={1}
              step={0.01}
              format="p0"
              value={this.state.settings.AssetAlternativeBidMaxAdjustment}
              onChange={(e) => {
                this.setSettings({ AssetAlternativeBidMaxAdjustment: e.value });
              }}
            />
          </div>}*/}
          <div className="k-form-field">
            <Checkbox
              label="Allow Alternate Pickup/Delivery Times (Non-Company Drivers Only)"
              checked={this.state.settings.AssetAlternativeTimesEnabled}
              onChange={(e) => this.setSettings({ AssetAlternativeTimesEnabled: e.value })}
            />
          </div>
          <br />
          <h5>Price Optimization</h5>
          <div className="k-form-field">
            <Checkbox
              label="Enable Quote UI"
              checked={this.state.settings.SmartbidEnabled}
              onChange={(e) => this.setSettings({ SmartbidEnabled: e.value })}
            />
          </div>
          {this.state.settings.SmartbidEnabled && <>
            <div className="k-form-field">
              <label>Linehaul Rate Per Mile Increase By Max</label>
              <NumericTextBox
                min={0}
                step={0.01}
                format={{ style: 'currency' }}
                value={this.state.settings.SmartbidIncreaseRatePerMile}
                onChange={(e) => {
                  this.setSettings({ SmartbidIncreaseRatePerMile: e.value });
                }}
              />
            </div>
            <div className="k-form-field">
              <label>Linehaul Rate Per Mile Decrease By Max</label>
              <NumericTextBox
                max={0}
                step={-0.01}
                format={{ style: 'currency' }}
                value={this.state.settings.SmartbidDecreaseRatePerMile}
                onChange={(e) => {
                  this.setSettings({ SmartbidDecreaseRatePerMile: e.value });
                }}
              />
            </div>
          </>}
        </div>

        <div className="alert alert-info mt-3" role="alert">
          Quote automation rules are now managed on the <Link to="/Quote/Rules" className="alert-link">Quote Rules</Link> page.
        </div>

        <div className='border rounded p-3'>
          <h5 className="mb-1">External Rate Markup Settings</h5>
          <div className="form-group row">
            <label className="col-form-label col-md-3">Airfreight Rates:</label>
            <div className="col-md-9">
              <div className="container">
                <div className="row">
                  <div className="col-md-3">
                    <label className="col-form-label">Minimum Markup Rate</label>
                    <NumericTextBox
                      min={0}
                      format={{ style: 'currency' }}
                      value={this.state.settings.AirfreightMinimumRate}
                      onChange={(e) => {
                        this.setSettings({ AirfreightMinimumRate: e.value });
                      }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="col-form-label">Maximum Markup Rate</label>
                    <NumericTextBox
                      min={0}
                      format={{ style: 'currency' }}
                      value={this.state.settings.AirfreightMaximumRate}
                      onChange={(e) => {
                        this.setSettings({ AirfreightMaximumRate: e.value });
                      }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="col-form-label">Markup %</label>
                    <NumericTextBox
                      min={0}
                      max={1}
                      step={0.01}
                      format={{ style: 'percent' }}
                      value={this.state.settings.AirfreightMarkup}
                      onChange={(e) => {
                        this.setSettings({ AirfreightMarkup: e.value });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-md-3">LTL Rates:</label>
            <div className="col-md-9">
              <div className="container">
                <div className="row">
                  <div className="col-md-3">
                    <label className="col-form-label">Minimum Markup Rate</label>
                    <NumericTextBox
                      min={0}
                      format={{ style: 'currency' }}
                      value={this.state.settings.EShipMinimumRate}
                      onChange={(e) => {
                        this.setSettings({ EShipMinimumRate: e.value });
                      }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="col-form-label">Maximum Markup Rate</label>
                    <NumericTextBox
                      min={0}
                      format={{ style: 'currency' }}
                      value={this.state.settings.EShipMaximumRate}
                      onChange={(e) => {
                        this.setSettings({ EShipMaximumRate: e.value });
                      }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="col-form-label">Markup %</label>
                    <NumericTextBox
                      min={0}
                      max={1}
                      step={0.01}
                      format={{ style: 'percent' }}
                      value={this.state.settings.EShipMarkup}
                      onChange={(e) => {
                        this.setSettings({ EShipMarkup: e.value });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <br />

        <div className='border rounded p-3'>
          <h5 className="mb-1">Carrier Load Board Settings</h5>
          <div className="k-form-field">
            <label>Default % of Revenue Offer Rate (Rivian = 83%):</label>
            <NumericTextBox
              required
              format="p"
              value={this.state.settings.LoadBoardMargin}
              onChange={(e) => this.setSettings({ LoadBoardMargin: e.value })}
              min={0}
              max={1}
              step={0.01}
            />
          </div>
        </div>

        <br />

        <div className='border rounded p-3'>
          <h5 className="mb-1">Carrier Tracking Settings</h5>
          <div className="k-form-field">
            <Checkbox
              label="Load One Carrier Driver App"
              checked={this.state.settings.CarrierDriverAppEnabled}
              onChange={(e) =>  this.setSettings({ CarrierDriverAppEnabled: e.value })}
            />
          </div>
          <div className="k-form-field">
            <Checkbox
              label="MacroPoint Lite"
              checked={this.state.settings.MacroPointLiteEnabled}
              onChange={(e) =>  this.setSettings({ MacroPointLiteEnabled: e.value })}
            />
          </div>
          <div className="k-form-field">
            <Checkbox
              label="Check Call Warning Email (heads-up to carrier 30 minutes before the check call interval is hit)"
              checked={this.state.settings.CheckCallWarningEnabled}
              onChange={(e) =>  this.setSettings({ CheckCallWarningEnabled: e.value })}
            />
          </div>
        </div>

        <br />

        <div className='border rounded p-3'>
          <h5 className="mb-1">AI Agent Settings</h5>
          <div className="k-form-field">
            <Checkbox
              label="Front Inbound Carrier Email Router"
              checked={this.state.settings.FrontInboundEmailRouterEnabled}
              onChange={(e) =>  this.setSettings({ FrontInboundEmailRouterEnabled: e.value })}
            />
          </div>
          <div className="k-form-field">
            <Checkbox
              label="Auto-Assign Tracking Missed Check Call Exceptions (Brokered & Ref3=DTW*)"
              checked={this.state.settings.AutoAssignTrackingMissedCheckCallEnabled}
              onChange={(e) =>  this.setSettings({ AutoAssignTrackingMissedCheckCallEnabled: e.value })}
            />
          </div>
          <div className="k-form-field">
            <label>Waiting Time Agent (kicks off at free-wait expiration; emails carrier/messages driver, drafts customer email)</label>
            <DropDownList
              data={waitingTimeAgentModes}
              textField="label"
              dataItemKey="value"
              value={waitingTimeAgentModes.find(x => x.value === this.state.settings.WaitingTimeAgentMode)}
              onChange={(e) =>  this.setSettings({ WaitingTimeAgentMode: e.value.value })}
            />
          </div>
          <div className="k-form-field">
            <label>Load Confirmation Call Agent (calls a driver who has not confirmed their dispatch in the LOD app)</label>
            <DropDownList
              data={loadConfirmationCallAgentModes}
              textField="label"
              dataItemKey="value"
              value={loadConfirmationCallAgentModes.find(x => x.value === this.state.settings.LoadConfirmationCallAgentMode)}
              onChange={(e) =>  this.setSettings({ LoadConfirmationCallAgentMode: e.value.value })}
            />
          </div>
        </div>

        <br />

        <Button onClick={this.saveSettings} themeColor="primary">Save Settings</Button>

        <br /><br />

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Load Map Vehicle Type Visibility</h5>
            <ul>
              <li>Cargo Vans can see other Cargo Vans and Sprinters</li>
              <li>Sprinters can see Cargo Vans, Sprinters, and Small Straights</li>
              <li>Small Straights can see Cargo Vans, Sprinters, and Small Straights</li>
              <li>Large Straights can see Small Straights and Large Straights</li>
              <li>All other vehicle types can only see their own vehicle type</li>
            </ul>
          </div>
        </div>

        <br />

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Turndown, Reset and Hours Given Back Policy</h5>

            {this.state.settings.ResetRules.map((rule, index) => <React.Fragment key={index}>
              <p className="font-weight-bold">{rule.HaulingCategory}</p>
              <ul>
                {rule.Rules.map((r, i) => <li key={i}>{r}</li>)}
                {rule.MPG === 3 && <li>Auto Offer Maximum Miles: {this.state.settings.AutoOfferMaxRegionalDistance}</li>}
                {rule.MPG === 4 && <li>Auto Offer Maximum Miles: {this.state.settings.AutoOfferMaxLocalOnlyDistance}</li>}
              </ul>
            </React.Fragment>)}
          </div>
        </div>

        <br />

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Dispatch Vehicle Suggestion Ranking</h5>
            <ol>
              <li>+30 pts - Driver Accepted Quote</li>
              <li>+15 pts - Driver Alt Accepted Quote</li>
              <li>+10 pts - Freight Will Fit Vehicle</li>
              <li>+5 pts - Vehicle is on reserve by you</li>
              <li>-5 pts - Vehicle is on reserve by someone else</li>
              <li>+5 pts - Vehicle Payload &gt; Order Total Weight</li>
              <li>-10 pts - Vehicle Payload &gt; 2x Order Total Weight</li>
              <li>+5 pts - Vehicle Type matches Requested Vehicle Type</li>
              <li>-10 pts - Vehicle Type is Tractor and Tractor was not requested</li>
              <li>-10 pts - Vehicle Type is not Tractor and Tractor was requested</li>
              <li>Scale up to +10 pts if Driver Score is 700 to 2000</li>
              <li>+5 pts - Vehicle is In-Service</li>
              <li>-10 pts - Vehicle is Out of Service</li>
              <li>-5 pts - Vehicle is On A Load</li>
              <li>Scale up to +20 pts if Vehicle Distance to Pickup is 200 to 0 miles out</li>
            </ol>
          </div>
        </div>

        <br />

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Additional Load Board Auto-Decline Rules</h5>
            <ul>
              <li>If pickup and delivery are both in Canada</li>
              <li>If the pickup or delivery is CAN and outside of Ontario</li>
              <li>We skip asset checks and don't auto decline if the pickup is more than 24 hours in the future</li>
            </ul>
          </div>
        </div>

        <br />

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Transit Time Calculation</h5>
            <ul>
              <li>MPH defaults to distance based speeds for Cargo Vans &amp; Sprinters (55 MPH at 600 miles or less, 50 MPH at 601-799 miles, and 45 MPH at 800+ miles), 55 for Small Straight, 50 for Large Straight/Tractor, and 45 for all other vehicle types, unless an MPH override is provided.</li>
              <li>Stop wait time is added for each stop after the first: 90 minutes per stop (solo) or 60 minutes per stop (team).</li>
              <li>60 minutes is added for each border crossing.</li>
              <li>Solo transit applies Hours of Service rules: up to 11 driving hours before a 10-hour break, and a 30-minute break on trips over 8 driving hours when no 10-hour break is needed. These are excluded for Cargo Vans &amp; Sprinters.</li>
              <li>Team transit does not add sleep/HOS break time.</li>
            </ul>
          </div>
        </div>

        <br />

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">PC*Miler Version</h5>
            <p>Transcend TMS: {this.state.settings.PCMilerVersion}</p>
          </div>
        </div>

        <br />

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Vehicle Reserve Times</h5>
            <p>When offering quotes to drivers, vehicles are automatically reserved based on the billing customer's industry vertical:</p>
            <ul>
              <li><b>10 Minutes:</b> Indirect/3PL, Energy and Utilities</li>
              <li><b>20 Minutes:</b> Manufacturing, Aerospace and Government, Automotive, Heavy Trucks, Life Sciences and Healthcare, Events and Entertainment</li>
              <li><b>15 Minutes:</b> Default for auto offers (when no industry vertical match or company setting override)</li>
              <li><b>20 Minutes:</b> Default for manual user offers (when no industry vertical match or company setting override)</li>
            </ul>
            <p><i>Note: Company specific quote settings will override these industry vertical defaults if configured above.</i></p>
          </div>
        </div>

        <br />

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Order Service Exception Types</h5>
            <ul>
              <li>
                <b>Tracking Team</b>
                <ul>
                  <li><b>Tracking Late</b> - When the next scheduled stop is overdue or projected to be late based on ETA calculations.</li>
                  <li><b>Needs Check Call</b> - When a trip has not received a new position within the required check call interval.</li>
                  <li><b>Waiting Time</b> - When waiting for over 60 minutes at a stop without a recently resolved or active service exception.</li>
                  <li><b>Needs Customer Update</b> - When Customer Notifications uses a Notify By of Phone/Website/Manual Email requiring a manual update.</li>
                </ul>
              </li>
              <li>
                <b>Accessorial Team</b>
                <ul>
                  <li><b>Accessorial Approval</b> - When a billing or general note is forwarded to the Accessorial Board.</li>
                </ul>
              </li>
              <li>
                <b>Admin Team</b>
                <ul>
                  <li><b>Paperwork Type Overwritten</b> - When AI automatically changes the paperwork type for an image</li>
                  <li><b>Print Flag Overwritten</b> - When AI automatically changes the print w/ invoice flag on an image.</li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  private setSettings(value: Partial<Settings>) {
    const settings = Object.assign({}, this.state.settings, value);
    this.setState({ settings });
  }

  private saveSettings() {
    this.setState({ loading: true });
    fetchApi('/api/Company/Settings', this.state.settings as Settings, 'post')
      .then(() => {
        this.setSettings({ CompanySettingsVersion: this.state.settings.CompanySettingsVersion + 1 });
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert("Unable to update settings");
      })
      .finally(() => {
        this.setState({ loading: false });
      })
  }
}
